import { Clock } from 'lucide-react'
import { useChannelFactoryUsage } from '@/api/endpoints/channels'
import { useActiveChannelFromUrl } from '@/hooks/useActiveChannel'
import styles from './GenerationTimeHint.module.scss'

interface GenerationTimeHintProps {
  /** ``video_projects.kind`` value the form will produce. e.g.
   * ``"curiosity"`` for the Viral form, ``"quiz"`` for the Quiz form.
   * Tied to the backend enum, not the route slug — the factory-usage
   * endpoint reports timings keyed by this value. */
  videoKind: string
}

/** Inline hint of "~78s baseado nas últimas gerações" rendered next to
 * the Gerar button on each factory form.
 *
 * Pulled from ``GET /channels/{slug}/factory-usage`` (median of
 * ``timings.total`` across the last 30 generations of that kind in the
 * active channel). Renders nothing when the channel hasn't produced
 * this factory yet — better than showing a fabricated estimate. */
export function GenerationTimeHint({ videoKind }: GenerationTimeHintProps) {
  const { active } = useActiveChannelFromUrl()
  const { data } = useChannelFactoryUsage(active?.slug)

  const row = data?.find((r) => r.kind === videoKind)
  const seconds = row?.median_generation_seconds
  if (seconds == null || !Number.isFinite(seconds) || seconds <= 0) return null

  const label = formatSeconds(seconds)

  return (
    <span
      className={styles.hint}
      role="note"
      title="Mediana de tempo de geração nas últimas 30 rodadas deste canal"
    >
      <Clock aria-hidden />
      <span>gerado em ~{label}</span>
      <span className={styles.fade}>· últimas {row?.count ?? 0} gerações</span>
    </span>
  )
}

function formatSeconds(seconds: number): string {
  // Rounded to whole minutes (minimum 1) — matches FactorySelector cards.
  const minutes = Math.max(1, Math.round(seconds / 60))
  return `${minutes}min`
}
